import { Link } from "lucide-react";
import { FC, useMemo } from "react";

import BuilderIcon from "@/assets/logos/builder.svg?react";
import LegitimateIcon from "@/assets/logos/LogoSymbol.svg?react";
import { SKU } from "@/data/types";
import { useFormValues } from "@/hooks/use-form-values";
import { SKUFormValues } from "@/lib/forms";

import { BuilderAttributes } from "./builder-attributes";
import { LegitimateAttributes } from "./legitimate-attributes";
import { PreviewExperience } from "./preview-experience";
import { RedirectAttributes } from "./redirect-attributes";
import { WebpageAttributes } from "./webpage-attributes";
import { WebpageBuilderSelector } from "./webpage-builder-selector";

type Props = {
  sku: SKU;
};

export const WebExperienceTab: FC<Props> = ({ sku }) => {
  const values = useFormValues<SKUFormValues>();

  const params = useMemo(() => {
    return { ...values, page_content: sku.page_content };
  }, [values, sku.page_content]);

  return (
    <div className="flex flex-col lg:flex-row gap-6 pt-4">
      <div className="flex-none lg:flex-1 lg:min-w-[320px] space-y-6">
        <WebpageBuilderSelector
          pageType={sku.page_type}
          skuId={sku.id}
          label={PageTypeMetadata[sku.page_type].label}
          description={PageTypeMetadata[sku.page_type].description}
          icon={PageTypeMetadata[sku.page_type].icon}
        />
        <WebpageAttributes
          sku={sku}
          content={PageTypeMetadata[sku.page_type].content}
        />
      </div>
      <PreviewExperience params={params} />
    </div>
  );
};

const PageTypeMetadata = {
  legitimate: {
    label: "Legitimate",
    description: "Build the digital experience using Legitimate",
    icon: <LegitimateIcon className="size-4" />,
    content: <LegitimateAttributes />,
  },
  redirect: {
    label: "Redirect",
    description: "Reroute to a different URL with tag parameters",
    icon: <Link className="size-4" />,
    content: <RedirectAttributes />,
  },
  builder_io: {
    label: "Builder.io",
    description: "Integrate with Builder, an external CMS",
    icon: <BuilderIcon className="size-4" />,
    content: <BuilderAttributes />,
  },
} as const;
